import React, { useEffect, useState, useRef } from "react";
import {
    agreementDetailsApi,
    agreementDetailsOtpApi,
    agreementDetailsEsignApi,
} from "../apis/customer/customer-apis";
import errorToast from "../utils/toast/errorToast";
import { useParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import InputFieldEsign from "../components/inputSignField";
import moment from "moment";

const ClientAgreementPage = () => {
    const params = useParams();
    const printRef = useRef();

    const handlePrint = () => {
        window.print();
    };

    const durationlist = [
        { label: "6 months", value: "6_months" },
        { label: "7 months", value: "7_months" },
        { label: "8 months", value: "8_months" },
    ];

    const mandatelist = [
        { label: "Mandate I", value: "Mandate_1" },
        { label: "Mandate II", value: "Mandate_2" },
        { label: "Mandate III", value: "Mandate_3" },
        { label: "Mandate IV", value: "Mandate_4" },
    ];

    const [viewData, setViewData] = useState("");

    const [esign, setEsign] = useState("");
    const [otp, setOtp] = useState("");

    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(false);

    const [resendTimer, setResendTimer] = useState(0);

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    useEffect(() => {
        if (resendTimer <= 0) return;

        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [resendTimer]);

    const sendOtp = async () => {
        if (otpLoading) return;

        if (!esign) {
            errorToast("E-signature not found");
            return;
        }
        try {
            setOtpLoading(true);

            const res = await agreementDetailsOtpApi({
                id: params?.id,
            });

            if (res.status === 200) {
                setOtp("");
                setShowOtpModal(true);

                // 60 second resend lock
                setResendTimer(60);
            } else {
                errorToast("Please try again later");
            }
        } catch (error) {
            console.log("error", error);
            errorToast("Please try again later");
        } finally {
            setOtpLoading(false);
        }
    };

    const generateAndDownloadPdf = async () => {
        if (!printRef.current) {
            errorToast("Agreement content not found");
            return;
        }

        if (!esign) {
            errorToast("E-signature not found");
            return;
        }

        setIsGeneratingPdf(true);

        let pdfContainer = null;

        try {
            // Clone the already-rendered agreement
            const pdfElement = printRef.current.cloneNode(true);

            // Create a temporary container
            pdfContainer = document.createElement("div");

            pdfContainer.style.position = "fixed";
            pdfContainer.style.left = "0";
            pdfContainer.style.top = "0";
            pdfContainer.style.width = "700px";
            pdfContainer.style.background = "#ffffff";
            pdfContainer.style.zIndex = "-9999";
            pdfContainer.style.opacity = "1";
            pdfContainer.style.pointerEvents = "none";

            pdfContainer.appendChild(pdfElement);

            document.body.appendChild(pdfContainer);

            // Remove buttons/actions from PDF
            pdfElement.querySelectorAll(".no-print").forEach((element) => {
                element.remove();
            });

            // Remove animation classes that can interfere with rendering
            pdfElement.classList.remove(
                "animate-in",
                "fade-in",
                "zoom-in",
                "duration-200",
            );

            // Make sure the agreement is fully visible
            pdfElement.style.position = "relative";
            pdfElement.style.width = "700px";
            pdfElement.style.maxWidth = "700px";
            pdfElement.style.height = "auto";
            pdfElement.style.maxHeight = "none";
            pdfElement.style.overflow = "visible";
            pdfElement.style.backgroundColor = "#ffffff";

            // Replace the signature input with the stored e-signature
            const signatureContainer =
                pdfElement.querySelector("#client-signature");

            if (signatureContainer) {
                signatureContainer.innerHTML = `
                <div
                    style="
                        height: 96px;
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 12px;
                        box-sizing: border-box;
                    "
                >
                    <img
                        src="${esign}"
                        alt="Client Signature"
                        style="
                            max-width: 180px;
                            max-height: 55px;
                            object-fit: contain;
                            display: block;
                        "
                    />

                    <div
                        style="
                            font-size: 10px;
                            color: #64748b;
                            margin-top: 5px;
                        "
                    >
                        Digitally Signed
                    </div>
                </div>
            `;
            }

            const truncateElements = pdfElement.querySelectorAll(".truncate");
            truncateElements.forEach((el) => {
                el.style.overflow = "visible";
                el.style.textOverflow = "unset";
                el.style.whiteSpace = "normal";
                el.style.wordBreak = "break-word";
            });

            const termsContainer = pdfElement.querySelector(
                "#pdf-terms-container",
            );
            if (termsContainer) {
                termsContainer.style.maxHeight = "none";
                termsContainer.style.height = "auto";
                termsContainer.style.overflow = "visible";
                termsContainer.style.display = "block";
                termsContainer.style.breakInside = "auto";
                termsContainer.style.pageBreakInside = "auto";
            } // Also make sure the inner agreement HTML is fully expanded const agreementPreview = pdfElement.querySelector( "#agreement-preview" ); if (agreementPreview) { agreementPreview.style.maxHeight = "none"; agreementPreview.style.height = "auto"; agreementPreview.style.overflow = "visible"; }
            /*
             * Wait for the browser to actually render
             * the cloned DOM before html2canvas captures it.
             */
            await new Promise((resolve) => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(resolve);
                });
            });

            // Wait for images inside the agreement
            const images = Array.from(pdfElement.querySelectorAll("img"));

            await Promise.all(
                images.map((img) => {
                    if (img.complete) {
                        return Promise.resolve();
                    }

                    return new Promise((resolve) => {
                        img.onload = resolve;
                        img.onerror = resolve;
                    });
                }),
            );

            // Small additional rendering delay
            await new Promise((resolve) => setTimeout(resolve, 300));

            const fileName = `Agreement-${ref || params?.id}.pdf`;

            await html2pdf()
                .set({
                    margin: [10, 10, 10, 10],

                    filename: fileName,

                    image: {
                        type: "jpeg",
                        quality: 0.98,
                    },

                    html2canvas: {
                        scale: 2,
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: "#ffffff",
                        logging: false,
                        scrollX: 0,
                        scrollY: 0,
                        windowWidth: 700,
                    },

                    jsPDF: {
                        unit: "mm",
                        format: "a4",
                        orientation: "portrait",
                        compress: true,
                    },

                    pagebreak: {
                        mode: ["css", "legacy"],
                    },
                })
                .from(pdfElement)
                .save();
        } catch (error) {
            console.error("PDF generation error:", error);
            errorToast("Unable to generate agreement PDF");
        } finally {
            // Remove temporary PDF DOM
            if (pdfContainer && document.body.contains(pdfContainer)) {
                document.body.removeChild(pdfContainer);
            }

            setIsGeneratingPdf(false);
        }
    };

    //TODO: use in otp pop up, otp is 6 digits
    const verifyOtp = async () => {
        if (otp.length !== 6) {
            errorToast("Please enter the 6 digit OTP");
            return;
        }

        if (!esign) {
            errorToast("Please provide your e-signature");
            return;
        }

        if (verifyLoading) return;

        try {
            setVerifyLoading(true);

            const res = await agreementDetailsEsignApi({
                id: params?.id,
                otp: otp,
                esign: esign,
            });

            if (res.status === 200) {
                // Generate PDF using the stored e-signature
                // await generateAndDownloadPdf();

                // Close OTP popup
                setShowOtpModal(false);

                // Mark agreement as completed
                setIsSubmitted(true);

                // Prevent the agreement from remaining visible
                setOtp("");
            } else {
                errorToast("Please try again later");
            }
        } catch (error) {
            console.log("error", error);

            errorToast(
                error?.response?.data?.message ||
                    "Unable to complete agreement. Please try again.",
            );
        } finally {
            setVerifyLoading(false);
        }
    };

    const viewFnc = async () => {
        try {
            const res = await agreementDetailsApi({
                id: params?.id,
            });

            if (res.status === 200) {
                var data = res.data?.data ?? {};

                // Agreement already completed
                if (data?.esign != null && data?.esign !== "") {
                    setIsSubmitted(true);
                    setEsign(data.esign);

                    return;
                }
                setViewData(res.data?.data);
                setref(data?.code);
                // settimestamp("April 14, 2026 • 11:15 AM");
                setclientName(data?.customer?.name);
                setrelationship(data?.relation_to_candidate);
                setclientId(data?.customer?.customer_code);
                setstartDate(
                    data?.agreement_start_dat
                        ? moment(data?.agreement_start_date).format(
                              "DD MMM YYYY",
                          )
                        : "--",
                );
                setduration(
                    durationlist?.find(
                        (item) => item?.value === data?.service_duration,
                    )?.label ?? "--",
                );
                setendDate(
                    data?.agreement_end_date
                        ? moment(data?.agreement_end_date).format("DD MMM YYYY")
                        : "--",
                );
                setmandate(
                    mandatelist?.find(
                        (item) => item?.value === data?.mandate_level,
                    )?.label ?? "--",
                );
                setmandateFee("₹" + (data?.mandate_fee || 0));
                setmatchFee("₹" + (data?.match_final_fee || 0));
                setemail(data?.email);
                setphone(data?.phone);
                setadvisor(data?.associatedEmployee?.name);
                setaddress(data?.address);
                setsignatureType("typed");
            }
        } catch (error) {
            console.log("error", error);

            errorToast("Please try again later");
        }
    };

    useEffect(() => {
        viewFnc();
    }, []);

    const esignInputObj = {
        key: "Esign",
        PlaceholderText: "E-Signature",
        value: "",
    };

    const [ref, setref] = useState();
    const [timestamp, settimestamp] = useState();
    const [clientName, setclientName] = useState();
    const [relationship, setrelationship] = useState();
    const [clientId, setclientId] = useState();
    const [startDate, setstartDate] = useState();
    const [duration, setduration] = useState();
    const [endDate, setendDate] = useState();
    const [mandate, setmandate] = useState();
    const [mandateFee, setmandateFee] = useState();
    const [matchFee, setmatchFee] = useState();
    const [email, setemail] = useState();
    const [phone, setphone] = useState();
    const [advisor, setadvisor] = useState();
    const [address, setaddress] = useState();
    const [signatureType, setsignatureType] = useState();

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-[#012a2c] via-[#013c3f] to-[#012a2c] flex items-center justify-center p-6">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center">
                    <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                        <svg
                            className="w-8 h-8 text-emerald-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>

                    <h1 className="text-2xl font-bold text-slate-900">
                        Agreement Submitted
                    </h1>

                    <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                        Your agreement has been successfully signed and
                        submitted. Your digital signature and OTP verification
                        have been recorded.
                    </p>

                    {ref && (
                        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-4">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Agreement Reference
                            </div>

                            <div className="mt-1 text-sm font-semibold text-slate-800">
                                {ref}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 text-[11px] text-slate-400">
                        This agreement has already been completed and cannot be
                        modified.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  reg-cover-bg p-10">
            <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center print:bg-white print:block print:static">
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
            @media print {
              /* 1. Hide everything else on the body */
              body * { visibility: hidden; }
              
              /* 2. Make the modal and its content visible */
              #printable-modal, #printable-modal * { visibility: visible; }

              /* 3. Strip modal styling for a flat document look */
              #printable-modal {
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
                height: auto !important;
                max-height: none !important;
                display: block !important;
                box-shadow: none !important; /* Remove shadow */
                border-radius: 0 !important; /* Remove rounded corners */
                margin: 0 !important;
                padding: 0 !important;
              }

              /* 4. Reset Fixed Overlay */
              .fixed.inset-0 {
                position: static !important;
                display: block !important;
              }

              /* 5. Force expansion of scrollable areas */
              .flex-1.overflow-y-auto, .terms-container {
                overflow: visible !important;
                height: auto !important;
                max-height: none !important;
                display: block !important;
              }

              /* 6. Hide Footer/Buttons */
              .no-print { display: none !important; }

              /* 7. Color Adjustments */
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          `,
                    }}
                />
                <div
                    ref={printRef}
                    id="printable-modal"
                    className="bg-white rounded-xl shadow-2xl w-full max-w-[700px] max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
                >
                    {/* Modal Header */}
                    <div className="relative flex-shrink-0 bg-slate-900 overflow-hidden">
                        <div className="p-6 md:px-7 md:py-5 flex items-start justify-between relative z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-[#C8A04A] to-[#9A7420] rounded-lg flex items-center justify-center shadow-lg">
                                    <svg
                                        viewBox="0 0 100 100"
                                        className="w-6 h-6 fill-none stroke-[#EDD078] stroke-[8]"
                                    >
                                        <path d="M50 8L92 50L50 92L8 50Z" />
                                        <path
                                            d="M50 34L66 50L50 66L34 50Z"
                                            fill="#EDD078"
                                            stroke="none"
                                        />
                                    </svg>
                                </div>
                                <div>
                                    <div className="font-serif text-white text-lg font-semibold leading-tight">
                                        House of Alliances
                                    </div>
                                    <div className="font-serif text-[10px] italic text-[#D4AE52]/70">
                                        Where Distinguished Families Meet Their
                                        Equals
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] font-bold tracking-[2px] uppercase text-white/40">
                                    Engagement Agreement
                                </div>
                                <div className="text-lg font-bold text-[#D4AE52]">
                                    {ref}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Modal Body */}
                    <div className="flex-1 overflow-y-auto p-6 md:p-7 space-y-5 text-slate-700">
                        {/* Parties */}
                        <div className="grid grid-cols-2 border border-slate-200 rounded-lg overflow-hidden">
                            <div className="p-4 border-r border-slate-200">
                                <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                                    Service Provider
                                </div>
                                <div className="text-sm font-bold text-slate-900">
                                    House of Alliances
                                </div>{" "}
                                <div className="text-[11.5px] text-slate-600">
                                    Premium Matchmaking Services
                                </div>
                                <div className="text-[11px] text-slate-400">
                                    Mumbai, Maharashtra · India
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                                    Client
                                </div>
                                <div className="text-sm font-bold text-slate-900">
                                    {clientName}
                                </div>
                                <div className="text-[11px] text-slate-400">
                                    {clientId}
                                </div>
                            </div>
                        </div>
                        {/* Meta Strip */}
                        <div className="grid grid-cols-4 bg-slate-50 border border-slate-200 rounded-lg divide-x divide-slate-200 overflow-hidden">
                            {[
                                { label: "Start Date", val: startDate },
                                { label: "Duration", val: duration },
                                { label: "End Date", val: endDate },
                                { label: "Mandate", val: mandate },
                            ].map((item, i) => (
                                <div key={i} className="px-3 py-2.5">
                                    <div className="text-[9px] font-bold uppercase tracking-tight text-slate-400 mb-1">
                                        {item.label}
                                    </div>
                                    <div className="text-xs font-semibold text-slate-800">
                                        {item.val}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Fees */}
                        <section>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-slate-100">
                                Fee Schedule
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-4">
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                        Mandate Fee
                                    </div>
                                    <div className="text-lg font-bold text-[#9A7420]">
                                        {mandateFee}
                                    </div>{" "}
                                    <div className="text-[10px] text-slate-400 mt-1">
                                        Payable in full at signing ·
                                        Non-refundable
                                    </div>
                                </div>
                                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                        Match Final Fee
                                    </div>
                                    <div className="text-lg font-bold text-slate-800">
                                        {matchFee}
                                    </div>
                                </div>
                            </div>
                        </section>
                        {/* Contact */}
                        <section>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-slate-100">
                                Client Contact Details
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-[12.5px]">
                                <div>
                                    <div className="text-[9px] font-bold text-slate-400 uppercase">
                                        Email
                                    </div>
                                    <div className="truncate font-medium">
                                        {email}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[9px] font-bold text-slate-400 uppercase">
                                        Phone
                                    </div>
                                    <div className="font-medium">{phone}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] font-bold text-slate-400 uppercase">
                                        Advisor
                                    </div>
                                    <div className="font-medium">{advisor}</div>
                                </div>
                            </div>{" "}
                            <div className="mt-3">
                                <div className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">
                                    Address
                                </div>
                                <div className="text-xs text-slate-600 leading-relaxed">
                                    {address}
                                </div>
                            </div>
                        </section>
                        {/* Terms */}
                        <section>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-slate-100">
                                Terms & Conditions Agreed
                            </div>
                            <div
                                id="pdf-terms-container"
                                className="terms-container bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] leading-relaxed text-slate-500 max-h-32 overflow-y-auto"
                            >
                                <div
                                    id="agreement-preview"
                                    dangerouslySetInnerHTML={{
                                        __html: viewData?.template
                                            ?.agreement_details,
                                    }}
                                />
                            </div>
                        </section>
                        {/* Signatures */}{" "}
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400  border-b border-slate-100">
                            Digital Signatures
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div
                                id="client-signature"
                                className="border border-slate-200 rounded-lg overflow-hidden"
                            >
                                <div className="text-[9px] font-bold uppercase bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-slate-500">
                                    Client Signature
                                </div>

                                {isGeneratingPdf ? (
                                    <div className="p-4 h-24 flex flex-col items-center justify-center">
                                        {esign && (
                                            <img
                                                src={esign}
                                                alt="Client Signature"
                                                className="max-w-[180px] max-h-[55px] object-contain"
                                            />
                                        )}

                                        <div className="text-[10px] text-slate-500 mt-1">
                                            Digitally Signed
                                        </div>
                                    </div>
                                ) : (
                                    <InputFieldEsign
                                        inputObj={esignInputObj}
                                        onUpdate={(esignData) => {
                                            setEsign(esignData);
                                        }}
                                    />
                                )}
                            </div>
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <div className="text-[9px] font-bold uppercase bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-slate-500">
                                    For House of Alliances
                                </div>
                                <div className="p-4 h-24 flex flex-col items-center justify-center">
                                    <div className="font-serif italic text-2xl text-slate-300">
                                        {viewData?.employee?.name}
                                    </div>
                                    <div className="text-[10px] text-slate-500 mt-1">
                                        Authorised Signatory
                                    </div>
                                </div>
                            </div>
                        </div>{" "}
                        {/* Footer Legal */}
                        <div className="text-[10px] text-slate-400 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3">
                            This agreement has been executed electronically. The
                            digital signature and OTP verification constitute a
                            valid, binding agreement under the{" "}
                            <strong>Information Technology Act, 2000</strong>.
                            Ref: {ref}.
                        </div>
                    </div>

                    {/* Footer Actions - HIDDEN IN PRINT via no-print class */}
                    <div className="p-4 border-t border-slate-100 flex gap-2 justify-end bg-white no-print">
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
                        >
                            Preview Agreement
                        </button>
                        <button
                            type="button"
                            onClick={sendOtp}
                            disabled={otpLoading || isGeneratingPdf}
                            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {otpLoading
                                ? "Sending OTP..."
                                : "Complete Agreement"}
                        </button>
                    </div>
                </div>
            </div>

            {showOtpModal && (
                <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
                        <div className="text-center">
                            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                                <svg
                                    className="w-6 h-6 text-emerald-600"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 11c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 20a6 6 0 0112 0"
                                    />
                                </svg>
                            </div>

                            <h2 className="mt-4 text-xl font-bold text-slate-900">
                                Verify Your Agreement
                            </h2>

                            <p className="mt-2 text-sm text-slate-500">
                                Enter the 6 digit OTP sent to your registered
                                mobile number.
                            </p>
                        </div>

                        <div className="mt-6">
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => {
                                    const value = e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6);

                                    setOtp(value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && otp.length === 6) {
                                        verifyOtp();
                                    }
                                }}
                                autoFocus
                                className="w-full text-center tracking-[0.7em] text-2xl font-bold border border-slate-300 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="••••••"
                            />
                        </div>

                        <div className="mt-5 text-center">
                            {resendTimer > 0 ? (
                                <div className="text-xs text-slate-400">
                                    Resend OTP in{" "}
                                    <span className="font-semibold text-slate-700">
                                        {resendTimer}s
                                    </span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={sendOtp}
                                    disabled={otpLoading}
                                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                                >
                                    {otpLoading ? "Sending..." : "Resend OTP"}
                                </button>
                            )}
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowOtpModal(false);
                                    setOtp("");
                                }}
                                disabled={verifyLoading}
                                className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={verifyOtp}
                                disabled={otp.length !== 6 || verifyLoading}
                                className="px-4 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {verifyLoading
                                    ? "Verifying..."
                                    : "Verify & Complete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientAgreementPage;
